import { useState } from 'react';

import { ArrowUp } from 'lucide-react';

import type { NodeView } from '@lemoncloud/eureka-chats-api';

interface NicknameFormProps {
    onJoin: (nickname: string) => Promise<NodeView | undefined>;
    isJoining: boolean;
}

/**
 * Nickname input form for joining chat room
 * Centered card layout with mobile-optimized input to prevent zoom
 * @param {NicknameFormProps} props - Component props
 * @param {Function} props.onJoin - Function to join chat room with nickname
 * @param {boolean} props.isJoining - Whether join operation is in progress
 * @returns {JSX.Element} Full-screen nickname input form
 */
export const NicknameForm = ({ onJoin, isJoining }: NicknameFormProps) => {
    const [nickname, setNickname] = useState('');

    /**
     * Handle form submission to join chat room
     */
    const handleSubmit = async () => {
        if (!nickname.trim()) return;
        await onJoin(nickname);
    };

    /**
     * Handle Enter key press to submit form
     * @param {React.KeyboardEvent} e - Keyboard event
     */
    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleSubmit();
        }
    };

    return (
        <div className="flex flex-col min-h-screen bg-white">
            <header className="w-full bg-white py-3 px-4 h-[52px]">
                <div
                    className="text-[28px] inline-block"
                    style={{
                        fontFamily: 'Aldrich',
                        letterSpacing: '-1.68px',
                        background: 'linear-gradient(90deg, #0B1933 3.75%, #102F6B 46.88%, #3968C3 94.37%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                        color: 'transparent',
                    }}
                >
                    Chat
                </div>
            </header>

            <div className="flex-1 flex items-start justify-center px-4 pt-16">
                <div className="w-full max-w-sm">
                    <div className="text-center mb-6">
                        <h2 className="text-lg font-medium text-[#081837] mb-2">채팅방 입장</h2>
                        <p className="text-sm text-[#53555B]">채팅에서 사용할 닉네임을 입력해주세요</p>
                    </div>

                    <div className="bg-white rounded-2xl border border-[#3968C3] p-4 shadow-sm">
                        <div className="flex gap-3">
                            <input
                                type="text"
                                placeholder="닉네임을 입력해주세요"
                                value={nickname}
                                onChange={e => setNickname(e.target.value)}
                                onKeyPress={handleKeyPress}
                                className={`flex-1 bg-white focus:outline-none text-base leading-6 text-[#081837] placeholder:text-[#84888F] transition-opacity ${
                                    isJoining ? 'opacity-50' : ''
                                }`}
                                style={{ fontSize: '16px' }}
                                disabled={isJoining}
                            />
                            <button
                                onClick={handleSubmit}
                                disabled={isJoining || !nickname.trim()}
                                className={`w-6 h-6 rounded-full flex items-center justify-center transition-colors ${
                                    nickname.trim() && !isJoining ? 'bg-[#081837]' : 'bg-[#EAEAEC]'
                                }`}
                            >
                                {isJoining ? (
                                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                ) : (
                                    <ArrowUp size={16} stroke={nickname.trim() ? '#FFFFFF' : '#BABCC0'} />
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
